import React, { useState, useEffect, useRef, useCallback } from 'react';
import { FileSpreadsheet, Upload, Download, Plus, Search, Users as UsersIcon, RefreshCw, Lock } from 'lucide-react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import useEscapeKey from '../../hooks/useEscapeKey';
import {
  Button, Badge, Avatar, Modal, ConfirmDialog, Input, Select,
  Table, THead, TBody, TR, TH, TD, Pagination, LoadingState, EmptyState,
} from '../../components/ui';

const ROLES = ['STUDENT', 'PROCTOR', 'ADMIN'];
const ROLE_VARIANT = { ADMIN: 'brand', PROCTOR: 'info', STUDENT: 'success' };

const defaultForm = { name: '', username: '', email: '', password: '', role: 'STUDENT' };

const generatePassword = () => {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789';
  return Array.from({ length: 10 }, () => chars[Math.floor(Math.random() * chars.length)]).join('');
};

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState(defaultForm);
  const [formLoading, setFormLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const fileInputRef = useRef(null);
  const [editUser, setEditUser] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [newCredentials, setNewCredentials] = useState(null);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [confirmBulkDelete, setConfirmBulkDelete] = useState(false);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 10 });
      if (search) params.append('search', search);
      if (roleFilter) params.append('role', roleFilter);
      const res = await api.get(`/users?${params.toString()}`);
      setUsers(res.data.data?.users || []);
      setTotalPages(res.data.data?.pagination?.totalPages || 1);
    } catch (err) {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchUsers(); setSelectedIds(new Set()); }, [page, search, roleFilter]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.username) {
      toast.error('Name and username are required');
      return;
    }
    if (!editUser && !formData.password) {
      toast.error('Password is required');
      return;
    }
    setFormLoading(true);
    try {
      if (editUser) {
        await api.put(`/users/${editUser._id || editUser.id}`, formData);
        toast.success('User updated successfully');
      } else {
        const res = await api.post('/users', formData);
        const creds = res.data?.data?.credentials;
        if (creds) {
          setNewCredentials(creds);
        } else {
          toast.success('User created successfully');
        }
      }
      setShowModal(false);
      setFormData(defaultForm);
      setEditUser(null);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save user');
    } finally {
      setFormLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/users/${deleteTarget}`);
      toast.success('User deleted');
      setDeleteTarget(null);
      fetchUsers();
    } catch (err) {
      toast.error('Failed to delete user');
    }
  };

  const toggleSelected = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const visibleIds = users.map((u) => u._id || u.id);
  const allVisibleSelected = visibleIds.length > 0 && visibleIds.every((id) => selectedIds.has(id));
  const someVisibleSelected = visibleIds.some((id) => selectedIds.has(id));

  const toggleSelectAll = () => {
    setSelectedIds((prev) => {
      if (allVisibleSelected) {
        const next = new Set(prev);
        visibleIds.forEach((id) => next.delete(id));
        return next;
      }
      return new Set([...prev, ...visibleIds]);
    });
  };

  const handleBulkDelete = async () => {
    try {
      const ids = Array.from(selectedIds);
      await Promise.all(ids.map((id) => api.delete(`/users/${id}`)));
      toast.success(`${ids.length} user(s) deactivated`);
      setSelectedIds(new Set());
      fetchUsers();
    } catch (err) {
      toast.error('Some users could not be deleted');
      fetchUsers();
    } finally {
      setConfirmBulkDelete(false);
    }
  };

  const handleBulkUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);

    setBulkLoading(true);
    const uploadPromise = api.post('/users/bulk-upload', fd, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });

    toast.promise(uploadPromise, {
      loading: 'Uploading and processing users...',
      success: (res) => {
        const { created, updated, errors } = res.data.data || {};
        const parts = [];
        if (created) parts.push(`${created} created`);
        if (updated) parts.push(`${updated} updated`);
        if (errors) parts.push(`${errors} skipped`);
        fetchUsers();
        return `Bulk upload complete: ${parts.join(', ') || 'No changes'}`;
      },
      error: (err) => err.response?.data?.message || 'Bulk upload failed'
    });

    try {
      await uploadPromise;
    } catch {
      // toast.promise handles it
    } finally {
      setBulkLoading(false);
      e.target.value = '';
    }
  };

  const handleDownloadSampleSheet = async () => {
    try {
      const res = await api.get('/users/sample-sheet', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'users_sample.xlsx';
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error('Failed to download sample sheet');
    }
  };

  const handleDownloadCredentials = async () => {
    // Scoped to the students currently loaded in the table (respects the active
    // search/role filter) — the backend refuses to export credentials without
    // an explicit userIds list, so this can never dump every student at once.
    const studentIds = users.filter((u) => u.role === 'STUDENT').map((u) => u._id || u.id);
    if (studentIds.length === 0) {
      toast.error('No students in the current view to export. Filter by role: STUDENT first.');
      return;
    }
    try {
      const res = await api.get('/users/download-credentials', {
        params: { userIds: studentIds.join(',') },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'credentials.csv';
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to download credentials');
    }
  };

  const handleUnlock = async (user) => {
    try {
      await api.put(`/users/${user._id || user.id}`, { loginLocked: false });
      toast.success('Login unlocked for ' + user.name);
      fetchUsers();
    } catch {
      toast.error('Failed to unlock login');
    }
  };

  const openEdit = (user) => {
    setEditUser(user);
    setFormData({ name: user.name, username: user.username, email: user.email || '', password: '', role: user.role });
    setShowModal(true);
  };

  const openAdd = () => {
    setEditUser(null);
    setFormData(defaultForm);
    setShowModal(true);
  };

  const closeModal = useCallback(() => {
    setShowModal(false);
    setEditUser(null);
    setFormData(defaultForm);
  }, []);

  useEscapeKey(closeModal, showModal);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">User Management</h2>
          <p className="text-slate-500 text-sm mt-1">Manage students, proctors, and admins</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <input ref={fileInputRef} type="file" accept=".csv,.xlsx,.xls" className="hidden" onChange={handleBulkUpload} />
          <Button variant="secondary" icon={FileSpreadsheet} onClick={handleDownloadSampleSheet} title="Download sample xlsx to fill and upload">
            Sample Sheet
          </Button>
          <Button variant="secondary" icon={Upload} loading={bulkLoading} onClick={() => fileInputRef.current?.click()}>
            {bulkLoading ? 'Uploading...' : 'Bulk Upload'}
          </Button>
          <Button variant="secondary" icon={Download} onClick={handleDownloadCredentials}>
            Download Credentials
          </Button>
          <Button variant="primary" icon={Plus} onClick={openAdd}>
            Add User
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="flex-1">
          <Input
            leftIcon={Search}
            placeholder="Search by name or username..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
          />
        </div>
        <Select value={roleFilter} onChange={(e) => { setRoleFilter(e.target.value); setPage(1); }} className="sm:w-48">
          <option value="">All Roles</option>
          {ROLES.map(r => <option key={r} value={r}>{r}</option>)}
        </Select>
      </div>

      {/* Bulk action bar -- only shown once something is selected */}
      {selectedIds.size > 0 && (
        <div className="flex items-center justify-between bg-themePrimary/5 border border-themePrimary/20 rounded-xl px-4 py-2.5">
          <span className="text-sm font-medium text-slate-700">{selectedIds.size} selected</span>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="ghost" onClick={() => setSelectedIds(new Set())}>Clear</Button>
            <Button size="sm" variant="danger" onClick={() => setConfirmBulkDelete(true)}>Delete Selected</Button>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
        {loading ? (
          <LoadingState label="Loading users..." />
        ) : users.length === 0 ? (
          <EmptyState icon={UsersIcon} title="No users found" />
        ) : (
          <Table>
            <THead>
              <TR>
                <TH className="w-10">
                  <input
                    type="checkbox"
                    className="rounded border-slate-300 text-themePrimary focus:ring-themePrimary/40 cursor-pointer"
                    checked={allVisibleSelected}
                    ref={(el) => { if (el) el.indeterminate = !allVisibleSelected && someVisibleSelected; }}
                    onChange={toggleSelectAll}
                    aria-label="Select all users on this page"
                  />
                </TH>
                <TH>Name</TH>
                <TH>Username</TH>
                <TH>Email</TH>
                <TH>Role</TH>
                <TH>Status</TH>
                <TH>Actions</TH>
              </TR>
            </THead>
            <TBody>
              {users.map((user) => {
                const id = user._id || user.id;
                return (
                  <TR key={id}>
                    <TD>
                      <input
                        type="checkbox"
                        className="rounded border-slate-300 text-themePrimary focus:ring-themePrimary/40 cursor-pointer"
                        checked={selectedIds.has(id)}
                        onChange={() => toggleSelected(id)}
                        aria-label={`Select ${user.name}`}
                      />
                    </TD>
                    <TD>
                      <div className="flex items-center gap-3">
                        <Avatar name={user.name} size="sm" />
                        <span className="font-medium text-slate-900">{user.name}</span>
                      </div>
                    </TD>
                    <TD>{user.username}</TD>
                    <TD>{user.email || '—'}</TD>
                    <TD><Badge variant={ROLE_VARIANT[user.role]}>{user.role}</Badge></TD>
                    <TD>
                      <div className="flex flex-col gap-1 items-start">
                        <Badge variant={user.isActive !== false ? 'success' : 'danger'}>
                          {user.isActive !== false ? 'Active' : 'Inactive'}
                        </Badge>
                        {user.role === 'STUDENT' && user.loginLocked && (
                          <Badge variant="warning" icon={Lock}>Login Locked</Badge>
                        )}
                      </div>
                    </TD>
                    <TD>
                      <div className="flex items-center gap-3 flex-wrap text-sm font-medium">
                        {user.role === 'STUDENT' && user.loginLocked && (
                          <button type="button" className="inline-flex items-center gap-1 text-amber-700 hover:underline" onClick={() => handleUnlock(user)}>
                            <RefreshCw className="w-3.5 h-3.5" /> Unlock
                          </button>
                        )}
                        <button type="button" className="text-themePrimary hover:underline" onClick={() => openEdit(user)}>Edit</button>
                        <button type="button" className="text-red-600 hover:underline" onClick={() => setDeleteTarget(id)}>Delete</button>
                      </div>
                    </TD>
                  </TR>
                );
              })}
            </TBody>
          </Table>
        )}

        {totalPages > 1 && (
          <div className="flex items-center justify-end px-6 py-4 border-t border-slate-200">
            <Pagination page={page} totalPages={totalPages} onChange={setPage} />
          </div>
        )}
      </div>

      {/* Add/Edit User Modal */}
      <Modal
        open={showModal}
        onClose={closeModal}
        title={editUser ? 'Edit User' : 'Add New User'}
        footer={
          <>
            <Button variant="outline" onClick={closeModal}>Cancel</Button>
            <Button variant="primary" type="submit" form="user-form" loading={formLoading}>
              {editUser ? 'Update User' : 'Create User'}
            </Button>
          </>
        }
      >
        <form id="user-form" onSubmit={handleSubmit} className="space-y-4">
          <Input
            label="Full Name *"
            value={formData.name}
            onChange={e => setFormData(p => ({ ...p, name: e.target.value }))}
            placeholder="Enter full name"
            required
          />
          <Input
            label="Username *"
            value={formData.username}
            onChange={e => setFormData(p => ({ ...p, username: e.target.value }))}
            placeholder="Enter username"
            required
          />
          <Input
            label="Email"
            type="email"
            value={formData.email}
            onChange={e => setFormData(p => ({ ...p, email: e.target.value }))}
            placeholder="Enter email address"
          />
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">
              Password {editUser ? '(leave blank to keep current)' : '*'}
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                value={formData.password}
                onChange={e => setFormData(p => ({ ...p, password: e.target.value }))}
                className="flex-1 border border-slate-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-themePrimary/30 focus:border-themePrimary font-mono"
                placeholder={editUser ? 'Leave blank to keep current' : 'Enter or auto-generate'}
              />
              <Button type="button" variant="outline" size="sm" onClick={() => setFormData(p => ({ ...p, password: generatePassword() }))}>
                Auto-generate
              </Button>
            </div>
          </div>
          <Select label="Role *" value={formData.role} onChange={e => setFormData(p => ({ ...p, role: e.target.value }))}>
            {ROLES.map(r => <option key={r} value={r}>{r}</option>)}
          </Select>
        </form>
      </Modal>

      {/* New-credentials modal — easier to copy/keep than a toast that disappears */}
      <Modal
        open={!!newCredentials}
        onClose={() => setNewCredentials(null)}
        title="User created"
        size="sm"
        footer={<Button variant="primary" onClick={() => setNewCredentials(null)}>Done</Button>}
      >
        {newCredentials && (
          <div className="space-y-3">
            <p className="text-sm text-slate-600">Share these credentials with the user — this is the only time the password is shown.</p>
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 space-y-2 font-mono text-sm">
              <div className="flex justify-between"><span className="text-slate-500">Username</span><span className="text-slate-900">{newCredentials.username}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Password</span><span className="text-slate-900">{newCredentials.password}</span></div>
            </div>
          </div>
        )}
      </Modal>

      <ConfirmDialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        variant="danger"
        title="Delete this user?"
        description="This will deactivate the account. This action cannot be undone from this screen."
        confirmLabel="Delete"
      />

      <ConfirmDialog
        open={confirmBulkDelete}
        onClose={() => setConfirmBulkDelete(false)}
        onConfirm={handleBulkDelete}
        variant="danger"
        title={`Delete ${selectedIds.size} selected user(s)?`}
        description="This will deactivate each selected account. This action cannot be undone from this screen."
        confirmLabel="Delete"
      />
    </div>
  );
};

export default UserManagement;
